import React, { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Clock, Filter, FileText, Building, Users, RefreshCw, FilterX } from 'lucide-react';
import clsx from 'clsx';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import DashboardFilterChip from '@components/common/DashboardFilterChip';
import SearchInput from '@components/common/SearchInput';
import { StatusIcon, Select } from '@components/common';
import { useAuth } from '@contexts/AuthContext';
import { dashboardService } from '@services';
import { useDashboardFilters } from '@hooks/useDashboardFilters';
import { hasRole, getDraftEvidencePermissions } from '@utils/permissionUtils';
import { GapItem, GapItemsFilters, ContractReference, ROLES } from '@app-types';
import { formatAssessmentId } from '@utils/exportUtils';
import { priorityToVariant, evidenceStatusToVariant } from '@utils/badgeMappers';
import {
  canShowUploadEvidenceButton,
  DRAFT_AWAITING_YOUR_SUBMISSION_LABEL,
  getProcessEvidenceLabel
} from '@utils/evidenceVisibility';
import { getEvidenceStatusLabel } from '@utils/referenceCatalog';
import DraftEvidenceActions from './DraftEvidenceActions';
import sharedStyles from './EvidenceDashboardShared.module.css';
import styles from './EvidenceManagementDashboard.module.css';

// Assessment filters a launch-page tile can set that the page's own controls cannot represent
const DASHBOARD_FILTER_KEYS = ['assessment_status', 'evidence_due_from', 'evidence_due_to', 'min_escalation_level'] as const;
const DASHBOARD_RESET_ALSO = ['status'] as const;

/** Dashboard-origin filters read from the URL; each is optional and only sent when present. */
function readGapItemsFilters(params: URLSearchParams): GapItemsFilters {
  const minEscalationLevel = params.get('min_escalation_level');
  return {
    assessment_status: params.get('assessment_status') || undefined,
    evidence_due_from: params.get('evidence_due_from') || undefined,
    evidence_due_to: params.get('evidence_due_to') || undefined,
    min_escalation_level: minEscalationLevel ? Number(minEscalationLevel) : undefined
  };
}

const EvidenceManagementDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  // Every row here is on a contract this CM manages or owns, so the uploader does not change their rights.
  const draftPermissions = getDraftEvidencePermissions(user, undefined);

  // Get filters from URL or use defaults
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';
  const priorityFilter = searchParams.get('priority') || 'all';
  const contractFilter = searchParams.get('contract') || 'all';
  const gapItemsFilters = useMemo(() => readGapItemsFilters(searchParams), [searchParams]);
  const { isActive: isDashboardFiltered, reset: resetDashboardFilters } = useDashboardFilters(DASHBOARD_FILTER_KEYS, {
    resetAlso: DASHBOARD_RESET_ALSO
  });

  // Update URL when filters change
  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all' && value !== '') {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Fetch gap items data
  const {
    data: gapItemsData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['contractManagerGapItems', gapItemsFilters],
    queryFn: () => dashboardService.getContractManagerGapItems(gapItemsFilters),
    enabled: !!user && hasRole(user, ROLES.CONTRACT_ROLE),
    staleTime: 0, // Always fetch fresh data - no stale cache
    refetchInterval: 30 * 1000, // Keep polling every 30 seconds for real-time updates
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  // Helper: Check if item matches search term
  const matchesSearch = (item: GapItem, searchTerm: string): boolean => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const assessmentIdFormatted = `AS-${String(item.assessmentId).padStart(6, '0')}`.toLowerCase();

    return (
      item.contractNumber?.toLowerCase().includes(searchLower) ||
      item.contractName?.toLowerCase().includes(searchLower) ||
      item.supplierName?.toLowerCase().includes(searchLower) ||
      item.process_name?.toLowerCase().includes(searchLower) ||
      item.assessmentName?.toLowerCase().includes(searchLower) ||
      assessmentIdFormatted.includes(searchLower) ||
      item.notes?.toLowerCase().includes(searchLower)
    );
  };

  // Helper: Check if item matches status filter
  const matchesStatus = (item: GapItem, statusFilter: string): boolean => {
    if (statusFilter === 'all') return true;

    if (statusFilter === 'action_required') {
      // Draft Evidence waits for this CM's submission, so it is action for them too.
      return ['none', 'rejected', 'mixed', 'draft'].includes(item.evidenceStatus);
    }

    return item.evidenceStatus === statusFilter;
  };

  // Process and filter data (frontend only filters, stats come from backend)
  const { filteredItems, uniqueContracts } = useMemo(() => {
    if (!gapItemsData) {
      return {
        filteredItems: [],
        uniqueContracts: []
      };
    }

    const allItems = gapItemsData.gap_items || [];

    // Filter items based on current filters
    const filtered = allItems.filter((item: GapItem) => {
      // Apply all filters
      if (!matchesSearch(item, searchTerm)) return false;
      if (!matchesStatus(item, statusFilter)) return false;

      // Priority filter
      if (priorityFilter !== 'all' && item.process_priority !== priorityFilter) return false;

      // Contract filter
      if (contractFilter !== 'all' && item.contractId.toString() !== contractFilter) return false;

      return true;
    });

    // Get unique contracts for filter dropdown
    const contracts = Array.from(
      new Map(
        allItems.map((item: GapItem) => [
          item.contractId,
          { id: item.contractId, number: item.contractNumber, name: item.contractName }
        ])
      ).values()
    );

    return {
      filteredItems: filtered,
      uniqueContracts: contracts
    };
  }, [gapItemsData, searchTerm, statusFilter, priorityFilter, contractFilter]);

  // Get stats directly from backend response (no frontend calculation)
  const stats = gapItemsData?.evidence_statistics || {
    needsEvidence: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    mixed: 0,
    awaitingSubmission: 0
  };

  const handleUploadEvidence = (item: GapItem) => {
    // Navigate to evidence upload form, preserving current filters for return navigation
    const query = searchParams.toString();
    const returnTo = query ? `/evidence?${query}` : '/evidence';
    navigate(`/evidence/upload/${item.id}`, { state: { returnTo } });
  };

  // Handle metric card click - reset search, priority and contract filters
  const handleMetricCardClick = (status: string) => {
    const newParams = new URLSearchParams();
    // Only set status, clear everything else (search, priority, contract)
    if (status && status !== 'all') {
      newParams.set('status', status);
    }
    setSearchParams(newParams);
  };

  // Handle refresh button click - invalidate and refetch data
  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['contractManagerGapItems'] });
  };

  // Handle clear filters button click - reset all filters
  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  if (!user || !hasRole(user, ROLES.CONTRACT_ROLE)) {
    return (
      <div className={sharedStyles.dashboardContainer}>
        <div className={sharedStyles.dashboardError}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access the Evidence Management Dashboard.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={sharedStyles.dashboardContainer}>
        <div className={sharedStyles.dashboardHeader}>
          <div className={sharedStyles.welcomeSection}>
            <h1>Evidence Management</h1>
            <p>Unable to load evidence data</p>
          </div>
        </div>
        <div className={sharedStyles.dashboardError}>
          <h2>Dashboard Unavailable</h2>
          <p>Unable to load evidence data. Please try refreshing the page.</p>
          <Button onClick={handleRefresh}>
            <RefreshCw size={16} />
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={sharedStyles.dashboardContainer}>
        <div className={sharedStyles.dashboardHeader}>
          <div className={sharedStyles.welcomeSection}>
            <h1>Evidence Management</h1>
            <p>Loading evidence data...</p>
          </div>
        </div>
        <div className={sharedStyles.loadingState}>
          <RefreshCw className="animate-spin" size={32} />
          <p>Loading evidence items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={sharedStyles.dashboardContainer}>
      {/* Welcome Header */}
      <div className={sharedStyles.dashboardHeader}>
        <div className={sharedStyles.welcomeSection}>
          <h1>Evidence Management</h1>
          <p>Manage evidence uploads for assessment gaps across all your contracts.</p>
        </div>
        <div className={sharedStyles.dashboardMeta}>
          <span className={sharedStyles.userRole}>Contract Role</span>
          <span className={sharedStyles.lastUpdated}>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {isDashboardFiltered && <DashboardFilterChip onReset={resetDashboardFilters} />}

      {/* Main Dashboard Grid */}
      <div className={sharedStyles.dashboardGrid}>
        {/* Key Metrics Row */}
        <div className={sharedStyles.dashboardRow}>
          <div className={styles.metricsGrid}>
            <button
              type="button"
              className={clsx(styles.metricCard, styles.metricOrange, styles.cursorPointer)}
              onClick={() => handleMetricCardClick('action_required')}
              title={`Click to filter items requiring action (${getProcessEvidenceLabel('none')} + Rejected + Mixed + ${DRAFT_AWAITING_YOUR_SUBMISSION_LABEL})`}
              data-test-id="evidence-metric-action-required"
            >
              <div className={styles.metricHeader}>
                <div className={styles.metricIcon}>
                  <StatusIcon status="warning" size={16} />
                </div>
                <div className={styles.metricLabel}>Action Required</div>
              </div>
              <div className={styles.metricValue}>
                {(stats.needsEvidence || 0) +
                  (stats.rejected || 0) +
                  (stats.mixed || 0) +
                  (stats.awaitingSubmission || 0)}
              </div>
              <div className={styles.metricSubtitle}>Gaps requiring upload, resubmission or submission</div>
            </button>

            <button
              type="button"
              className={clsx(styles.metricCard, styles.metricBlue, styles.cursorPointer)}
              onClick={() => handleMetricCardClick('pending')}
              data-test-id="evidence-metric-pending"
            >
              <div className={styles.metricHeader}>
                <div className={styles.metricIcon}>
                  <Clock size={16} />
                </div>
                <div className={styles.metricLabel}>{getEvidenceStatusLabel('pending')}</div>
              </div>
              <div className={styles.metricValue}>{stats.pending}</div>
              <div className={styles.metricSubtitle}>Evidence pending assessor review</div>
            </button>

            <button
              type="button"
              className={clsx(styles.metricCard, styles.metricGreen, styles.cursorPointer)}
              onClick={() => handleMetricCardClick('approved')}
              data-test-id="evidence-metric-approved"
            >
              <div className={styles.metricHeader}>
                <div className={styles.metricIcon}>
                  <StatusIcon status="success" size={16} />
                </div>
                <div className={styles.metricLabel}>{getEvidenceStatusLabel('approved')}</div>
              </div>
              <div className={styles.metricValue}>{stats.approved}</div>
              <div className={styles.metricSubtitle}>Evidence approved by assessors</div>
            </button>

            <button
              type="button"
              className={clsx(styles.metricCard, styles.metricRed, styles.cursorPointer)}
              onClick={() => handleMetricCardClick('rejected')}
              data-test-id="evidence-metric-rejected"
            >
              <div className={styles.metricHeader}>
                <div className={styles.metricIcon}>
                  <StatusIcon status="error" size={16} />
                </div>
                <div className={styles.metricLabel}>{getEvidenceStatusLabel('rejected')}</div>
              </div>
              <div className={styles.metricValue}>{stats.rejected}</div>
              <div className={styles.metricSubtitle}>Evidence requiring resubmission</div>
            </button>

            <button
              type="button"
              className={clsx(styles.metricCard, styles.metricPurple, styles.cursorPointer)}
              onClick={() => handleMetricCardClick('all')}
            >
              <div className={styles.metricHeader}>
                <div className={styles.metricIcon}>
                  <FileText size={16} />
                </div>
                <div className={styles.metricLabel}>Total Gaps</div>
              </div>
              <div className={styles.metricValue}>{gapItemsData?.total_gap_items || 0}</div>
              <div className={styles.metricSubtitle}>All gaps across contracts</div>
            </button>
          </div>
        </div>

        {/* Filters and Evidence List Row */}
        <div className={sharedStyles.dashboardRow}>
          <div className={sharedStyles.dashboardCard}>
            <div className={sharedStyles.cardHeader}>
              <h3>
                <Filter size={18} />
                Evidence Management
              </h3>
              <div className={sharedStyles.headerActions}>
                {(searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || contractFilter !== 'all') && (
                  <Button variant="ghost" onClick={handleClearFilters}>
                    <FilterX size={16} />
                    Clear Filters
                  </Button>
                )}
                <Button variant="ghost" onClick={handleRefresh}>
                  <RefreshCw size={16} />
                  Refresh
                </Button>
              </div>
            </div>

            <div className={sharedStyles.cardContent}>
              {/* Filters */}
              <div className={sharedStyles.filtersGrid}>
                <div className={sharedStyles.filterGroup}>
                  <label htmlFor="evidence-mgmt-search">Search</label>
                  <SearchInput
                    id="evidence-mgmt-search"
                    value={searchTerm}
                    onChange={(value) => updateFilter('search', value)}
                    placeholder="Search contracts, suppliers, processes, assessments..."
                  />
                </div>

                <div className={sharedStyles.filterGroup}>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(value) => updateFilter('status', value)}
                    options={[
                      { value: 'all', label: 'All Statuses' },
                      { value: 'action_required', label: 'Action Required' },
                      { value: 'draft', label: DRAFT_AWAITING_YOUR_SUBMISSION_LABEL },
                      { value: 'pending', label: getEvidenceStatusLabel('pending') },
                      { value: 'approved', label: getEvidenceStatusLabel('approved') },
                      { value: 'rejected', label: getEvidenceStatusLabel('rejected') }
                    ]}
                    className={sharedStyles.filterSelect}
                    dataTestId="evidence-filter-status"
                  />
                </div>

                <div className={sharedStyles.filterGroup}>
                  <Select
                    label="Priority"
                    value={priorityFilter}
                    onChange={(value) => updateFilter('priority', value)}
                    options={[
                      { value: 'all', label: 'All Priorities' },
                      { value: 'High', label: 'High Priority' },
                      { value: 'Medium', label: 'Medium Priority' },
                      { value: 'Low', label: 'Low Priority' }
                    ]}
                    className={sharedStyles.filterSelect}
                    dataTestId="evidence-filter-priority"
                  />
                </div>

                <div className={sharedStyles.filterGroup}>
                  <Select
                    label="Contract"
                    value={contractFilter}
                    onChange={(value) => updateFilter('contract', value)}
                    options={[
                      { value: 'all', label: 'All Contracts' },
                      ...uniqueContracts.map((contract: ContractReference) => ({
                        value: String(contract.id),
                        label: `${contract.number} - ${contract.name}`
                      }))
                    ]}
                    className={sharedStyles.filterSelect}
                    dataTestId="evidence-filter-contract"
                  />
                </div>
              </div>

              {/* Evidence List */}
              <div className={sharedStyles.evidenceListSection}>
                <div className={sharedStyles.listHeader}>
                  <span className={sharedStyles.itemsCount}>
                    {filteredItems.length} of {gapItemsData?.total_gap_items || 0} items
                  </span>
                </div>

                {filteredItems.length === 0 ? (
                  <div className={sharedStyles.emptyState} data-test-id="evidence-management-empty-state">
                    {stats.needsEvidence === 0 ? (
                      <>
                        <StatusIcon status="success" size={48} />
                        <h4>All Evidence Uploaded!</h4>
                        <p>All gaps have evidence uploaded. Great work!</p>
                      </>
                    ) : (
                      <>
                        <StatusIcon status="warning" size={48} />
                        <h4>No Items Match Filters</h4>
                        <p>Try adjusting your filters to see more results.</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={sharedStyles.gapItemsList}>
                    {filteredItems.map((item: GapItem) => (
                      <div key={item.id} className={sharedStyles.gapItem} data-test-id="evidence-gap-item">
                        <div className={sharedStyles.itemHeader}>
                          <div className={sharedStyles.itemInfo}>
                            <div className={sharedStyles.itemTitle}>
                              {item.process_name || 'Process Name'}
                              {item.process_priority && (
                                <Badge variant={priorityToVariant(item.process_priority)}>
                                  {item.process_priority} Priority
                                </Badge>
                              )}
                            </div>
                            <div className={sharedStyles.itemMeta}>
                              <div className={sharedStyles.metaItem}>
                                <Building size={14} />
                                <span>{item.supplierName}</span>
                              </div>
                              <div className={sharedStyles.metaItem}>
                                <FileText size={14} />
                                <button
                                  className="table-link"
                                  onClick={() => navigate(`/contracts/${item.contractId}`)}
                                  title="View contract details"
                                >
                                  {item.contractNumber}
                                </button>
                              </div>
                              <div className={sharedStyles.metaItem}>
                                <Users size={14} />
                                <button
                                  className="table-link"
                                  onClick={() => navigate(`/assessments/${item.assessmentId}`)}
                                  title="View assessment details"
                                >
                                  {formatAssessmentId(item.assessmentId)}: {item.assessmentName}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className={sharedStyles.itemActions}>
                            <div className={sharedStyles.evidenceStatus}>
                              {item.hasEvidence ? (
                                <Badge variant={evidenceStatusToVariant(item.evidenceStatus)}>
                                  {item.evidenceStatus === 'draft'
                                    ? DRAFT_AWAITING_YOUR_SUBMISSION_LABEL
                                    : getProcessEvidenceLabel(item.evidenceStatus)}
                                </Badge>
                              ) : (
                                <Badge variant={evidenceStatusToVariant('none')}>{getProcessEvidenceLabel('none')}</Badge>
                              )}
                            </div>
                            {canShowUploadEvidenceButton(item.evidenceStatus) && (
                              <Button variant="primary" size="sm" onClick={() => handleUploadEvidence(item)}>
                                <Upload size={14} />
                                {item.evidenceStatus === 'none' ? 'Upload Evidence' : 'Resubmit Evidence'}
                              </Button>
                            )}
                            {item.evidenceStatus === 'draft' && (
                              <DraftEvidenceActions
                                assessmentItemId={item.id}
                                processName={item.process_name ?? 'this process'}
                                permissions={draftPermissions}
                              />
                            )}
                          </div>
                        </div>
                        {item.notes && (
                          <div className={sharedStyles.itemNotes}>
                            <strong>Assessment Notes:</strong> {item.notes}
                          </div>
                        )}
                        {item.evidenceStatus === 'rejected' && item.lastReviewNotes && (
                          <div className={clsx(sharedStyles.itemNotes, sharedStyles.rejectedNotes)}>
                            <strong>⚠️ Assessor Feedback:</strong> {item.lastReviewNotes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvidenceManagementDashboard;
